/**
 * Client for the `campaign` edge function (v2). No component talks to the batch_campaigns or
 * batch_campaign_contacts tables, their RPCs, or campaign-orchestrator directly. Field names are the column
 * names. Keep in sync with `supabase/functions/campaign/` and `components/BatchCall/campaign-file.ts`.
 *
 * A campaign is made in three steps (`createCampaignFromFile`): a slot is reserved for its contacts file, the
 * browser sends the file (NDJSON, one contact a line, the numbers already +E.164) to Storage through the slot's
 * resumable link, and the campaign is created with the slot's `import_id`. The worker then imports the file in
 * the background; the campaign is a draft until it has, and cannot be started before.
 */

import { api } from "@/lib/api-client";
import { type ListParams, MAX_PAGE_SIZE, type PagedResult } from "@/lib/list-query";
import type { BusinessHoursConfig } from "@/lib/business-hours";
import { sendFile, type UploadTarget } from "@/lib/resumable-upload";

const BASE = "/campaign";

export type CampaignStatus = "draft" | "scheduled" | "warmup" | "in-progress" | "paused" | "completed" | "cancelled" | "dormant";

export type ImportStatus = "uploading" | "queued" | "importing" | "done" | "failed";

/** A campaign's contacts file and how far its import is: `lines_read` of `total_lines` (null until counted). */
export interface CampaignImport {
  id: string;
  status: ImportStatus;
  expected_count: number | null;
  total_lines: number | null;
  lines_read: number;
  contacts_added: number;
  rows_skipped: number;
  error_message: string | null;
}

/** A single campaign's import says more: the file, and the first 100 lines skipped with why (line 1 = the file's first). */
export interface CampaignImportDetail extends CampaignImport {
  file_name: string;
  file_size: number;
  skipped: { line: number; reason: string }[];
}

export interface Campaign {
  id: string;
  workspace_id: string;
  campaign_name: string;
  /** Null once the number was deleted — only ever on a finished campaign. */
  phone_number_id: string | null;
  /** Null once the agent was deleted — only ever on a finished campaign. */
  agent_id: string | null;
  status: CampaignStatus;
  total_contacts: number;
  completed_calls: number;
  successful_calls: number;
  failed_calls: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  max_retries: number;
  /** Minutes before each retry. */
  retry_intervals: number[];
  business_hours: BusinessHoursConfig | null;
  /** Set when dormant: the next instant it may dial. */
  dormant_until: string | null;
  /** Set on every move into warmup; `started_at` stays the first start. */
  last_warmup_at: string | null;
  default_country: string | null;
  /** Null for a campaign made before v2. */
  import: CampaignImport | null;
}

/** One campaign as `GET /campaign/:id` answers it. */
export type CampaignDetail = Omit<Campaign, "import"> & { import: CampaignImportDetail | null };

export interface CampaignContactCallLog {
  analysis_data: Record<string, unknown> | null;
  summary: string | null;
  duration: number | null;
  disconnection_reason: string | null;
}

export interface CampaignContact {
  id: string;
  campaign_id: string;
  phone_number: string;
  contact_name: string | null;
  custom_data: Record<string, unknown> | null;
  status: "pending" | "calling" | "completed" | "failed" | "retry_scheduled" | "cancelled";
  call_log_id: string | null;
  attempt_count: number;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  disconnection_reason: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  call_log: CampaignContactCallLog | null;
}

export type CampaignContactStats = Record<CampaignContact["status"], number>;

/** One call-analysis filter, as the dashboard's filter chips hold it; a bound or value of "" is none. */
export type AnalysisFilter = Record<string, unknown> & { name: string; type: string };

export interface ContactsQuery {
  /** From 1. */
  page: number;
  /** Up to 1,000. */
  size: number;
  durationMin?: number | null;
  durationMax?: number | null;
  analysisFilters?: AnalysisFilter[];
}

/** The settings a campaign is created with; its contacts come from the file. */
export interface NewCampaign {
  workspace_id: string;
  campaign_name: string;
  agent_id: string;
  phone_number_id: string;
  max_retries: number;
  retry_intervals: number[];
  scheduled_at: string | null;
  business_hours: BusinessHoursConfig | null;
  default_country: string | null;
}

/** What may change while a campaign is a draft, scheduled or paused. */
export type CampaignPatch = Partial<
  Pick<Campaign, "campaign_name" | "phone_number_id" | "max_retries" | "retry_intervals" | "scheduled_at" | "business_hours">
>;

export type CampaignAction = "start" | "pause" | "resume" | "cancel";

/** A reserved slot for a contacts file, and where the file goes. */
export interface ReservedImport {
  import: CampaignImport;
  upload: UploadTarget & { signed_url: string; expires_at: string };
}

/** One page of a workspace's campaigns, newest first, each with its import's progress. */
export const listCampaigns = (workspaceId: string, params: ListParams, status?: CampaignStatus[]) =>
  api.postPage<Campaign>(`${BASE}/list`, params, { workspace_id: workspaceId, status });

/**
 * Every campaign of the workspace, a page of the server's ceiling at a time, until it holds the whole set — or a
 * page comes back empty, so a total that shrinks mid-read cannot loop forever.
 */
export async function listAllCampaigns(workspaceId: string): Promise<Campaign[]> {
  const campaigns: Campaign[] = [];
  let page = 1;
  let last: PagedResult<Campaign>;
  do {
    last = await listCampaigns(workspaceId, { page: page++, size: MAX_PAGE_SIZE, search: "" });
    campaigns.push(...last.rows);
  } while (last.rows.length > 0 && campaigns.length < last.total);
  return campaigns;
}

export const getCampaign = (id: string) => api.getItem<CampaignDetail>(`${BASE}/${id}`);

export const reserveImport = (workspaceId: string, file: { file_name: string; file_size: number; contact_count: number }) =>
  api.post<ReservedImport>(`${BASE}/imports`, { workspace_id: workspaceId, ...file });

export const createCampaign = (body: NewCampaign & { import_id: string }) => api.post<Campaign>(BASE, body);

/**
 * The campaign and its contacts: the file reserved, sent, then the campaign created with it. `onProgress` hears
 * the upload's percent. A refusal at any step rejects with the function's sentence; an upload that fails leaves
 * no campaign behind (its slot goes once its link expires).
 */
export async function createCampaignFromFile(
  settings: NewCampaign,
  file: { name: string; ndjson: string; contacts: number },
  onProgress: (percent: number) => void,
): Promise<Campaign> {
  const body = new Blob([file.ndjson], { type: "application/x-ndjson" });
  const reserved = await reserveImport(settings.workspace_id, {
    file_name: file.name,
    file_size: body.size,
    contact_count: file.contacts,
  });
  await sendFile(reserved.upload, body, onProgress);
  return await createCampaign({ ...settings, import_id: reserved.import.id });
}

export const updateCampaign = (id: string, patch: CampaignPatch) => api.put<Campaign>(`${BASE}/${id}`, patch);

/** Start, pause, resume or cancel; answers the campaign as the action left it. */
export const runCampaignAction = (id: string, action: CampaignAction) => api.post<Campaign>(`${BASE}/${id}/${action}`, {});

/** A failed import again, from where it stopped. */
export const retryImport = (id: string) => api.post<Campaign>(`${BASE}/${id}/import/retry`, {});

/** A page of the campaign's contacts, filtered by call length and call analysis. */
export const listCampaignContacts = (id: string, { page, size, durationMin, durationMax, analysisFilters }: ContactsQuery) =>
  api.postPage<CampaignContact>(`${BASE}/${id}/contacts`, { page, size, search: "" }, {
    duration_min: durationMin ?? null,
    duration_max: durationMax ?? null,
    analysis_filters: analysisFilters ?? [],
  });

/** The largest page the function serves: the CSV export reads 1,000 contacts a request. */
export const CONTACTS_PAGE_MAX = 1_000;

/** Every contact matching the filters, a page of 1,000 at a time, so an export is never cut short. */
export async function listAllCampaignContacts(
  id: string,
  filters: Omit<ContactsQuery, "page" | "size">,
): Promise<CampaignContact[]> {
  const contacts: CampaignContact[] = [];
  let page = 1;
  let last: PagedResult<CampaignContact>;
  do {
    last = await listCampaignContacts(id, { ...filters, page: page++, size: CONTACTS_PAGE_MAX });
    contacts.push(...last.rows);
  } while (last.rows.length > 0 && contacts.length < last.total);
  return contacts;
}

/** The whole campaign's contacts per status, whatever filters the page has on. */
export const getCampaignContactStats = (id: string) => api.getItem<CampaignContactStats>(`${BASE}/${id}/stats`);
